/**
 * AudioClip Behavior
 *
 * Loads and plays a standalone audio clip via an HTMLAudioElement. The clip
 * URL comes from the SceneObject's `src` field (mp3/wav/ogg; any
 * browser-decodable format added to AUDIO_EXTENSIONS works).
 *
 * Plays through the element's default sink. Add an `AudioSource` behavior
 * on the same SceneObject to route the audio through the Web Audio graph for
 * volume, 3D positioning, and distance attenuation — AudioSource detects this
 * behavior the same way it detects Video.
 *
 * Autoplay: browsers refuse <audio>.play() before a user gesture. A play the
 * policy refuses (e.g. playOnStart at load) retries once on the next gesture
 * through AudioManager, unless it was paused or stopped meanwhile.
 *
 * Start offset (`startAt`, RECORDINGS_SPEC §4.4): the clip lives in a time
 * base `t` where media sample 0 sounds at `t = startAt`. `seek(t)` places the
 * media at `max(0, t − startAt)`. For `t < startAt` the clip is WAITING: the
 * element is paused at 0 and the clip counts the remaining `startAt − t` down
 * on scene time (the engine delta — the same clock a Timeline advances on),
 * starting the element itself when the count reaches 0. The count only runs
 * while the clip has been told to play: `pause()` freezes it, `play()`
 * resumes it, and `seek()` recomputes it (a seek past `startAt` while waiting
 * starts the element immediately at the mapped position). There is no
 * per-frame coupling to the Timeline and no new Timeline→Playable API; the
 * Timeline's usual seek + play on play/seek/loop is all the clip needs.
 * Scene time is not scaled by the Timeline's `speed` (Playables never see
 * it), which matches how every other Playable ignores `speed`.
 */

import type { Scene } from "@babylonjs/core/scene";
import { Observable, type Observer } from "@babylonjs/core/Misc/observable";
import type { Nullable } from "@babylonjs/core/types";
import { TraceBehavior } from "./tracebehavior";
import { AudioClipData } from "../scenedata/behaviors/audioclipdata";
import { Logger } from "../utils/Logger";
import type { SceneObject } from "../core/SceneObject";
import { mediaElementClockTime, type Playable, type PlayableClock, type PlayableSource, type PlayableVolume } from "./playable";

export class AudioClip extends TraceBehavior<AudioClipData> implements Playable, PlayableSource, PlayableVolume, PlayableClock {
    static readonly behaviorType = "AudioClip" as const;

    private _audio: HTMLAudioElement | null = null;
    private _resolvedUrl: string | null = null;
    /** True if the URL came from `URL.createObjectURL` and must be revoked on detach. */
    private _ownsObjectUrl = false;
    private _loaded = false;
    /**
     * An explicit play() that arrived before there was an element to play
     * (a clip whose source is only set at runtime via setSource). Honoured by
     * the next entry point that can start playback — never held back by
     * visibility, since an explicit play() on a loaded clip isn't either —
     * and cancelled by pause(), stop() or leaving play mode.
     */
    private _pendingPlayback = false;
    /** Cancels the pending on-gesture retry of a play the autoplay policy refused. */
    private _cancelBlockedRetry: (() => void) | null = null;
    /**
     * Bumped by every play/pause/stop, so a play() refusal that settles after
     * the clip was paused or replayed does not schedule a stale retry.
     */
    private _playRequest = 0;
    /**
     * Bumped on every setSource call. The Promise resolver captures the value
     * at construction time and bails if it no longer matches — protects against
     * rapid back-to-back setSource calls resolving the earlier promise with the
     * later source's metadata.
     */
    private _loadGeneration = 0;
    /**
     * Seconds of clip time left before `startAt` while positioned before it,
     * or null when positioned at/after `startAt` (the element's position is
     * then authoritative). Initialized for t = 0.
     */
    private _waitRemaining: number | null = null;
    /** Scene-time countdown toward `startAt`; non-null only while told to play and waiting. */
    private _waitObserver: Nullable<Observer<Scene>> = null;

    constructor(sceneObject: SceneObject, data: AudioClipData) {
        super(sceneObject, data);
        const startAt = this._startAt();
        this._waitRemaining = startAt > 0 ? startAt : null;
    }

    /**
     * Fires once when the underlying <audio> element has metadata (duration).
     * Subscribers receive the HTMLAudioElement and may attach Web Audio nodes
     * (e.g. AudioSource). Subscribers added after the fire-once should check
     * `getMediaElement()` directly.
     */
    public onMediaElementReadyObservable = new Observable<HTMLAudioElement>();

    /** Get the underlying HTMLAudioElement, or null if not yet loaded. */
    public getMediaElement(): HTMLAudioElement | null {
        return this._audio;
    }

    load(): Promise<void> {
        // Resolve URL — try cached blob first (covers local paths + URL mappings),
        // fall back to direct URL. Mirrors Video's resolution path.
        let url = this.sceneObject.src ?? "";
        const blob = this.resources.getAsset(url);
        if (blob) {
            url = URL.createObjectURL(blob);
            this._ownsObjectUrl = true;
        } else {
            // Nothing cached: a folder document's relative src resolves
            // against the document's own URL (spec §2.3).
            url = this.resources.resolveAssetUrl(url) ?? url;
        }

        if (!url) {
            Logger.warn(`[AudioClip] No audio source specified for "${this.sceneObject.name}"`);
            return Promise.resolve();
        }

        this._resolvedUrl = url;

        const el = new window.Audio();
        el.preload = "metadata";
        // crossOrigin must be set before src — setting it after triggers a
        // second fetch and risks a CORS-less response being cached, which
        // makes ctx.createMediaElementSource() throw SecurityError later.
        el.crossOrigin = "anonymous";
        el.src = url;
        el.loop = this._resolved.loop !== false;
        this._audio = el;
        // Place the fresh element at t = 0 in the clip's time base. Skipped
        // for startAt = 0, where 0 is already the element's position.
        if (this._startAt() !== 0) this._positionAt(0);

        const onReady = () => {
            if (this._loaded) return;
            this._loaded = true;
            this.onMediaElementReadyObservable.notifyObservers(el);

            // Metadata can arrive after the object was hidden (e.g. a Pages
            // deck deactivated its page while the fetch was in flight), which
            // _shouldStartPlayback's live effectiveActive read covers (#937).
            this._startPlaybackIfDue();
        };

        if (el.readyState >= 1 /* HAVE_METADATA */) {
            onReady();
        } else {
            el.addEventListener('loadedmetadata', onReady, { once: true });
        }

        el.addEventListener('error', () => {
            Logger.warn(`[AudioClip] Failed to load "${this.sceneObject.name}": ${el.error?.message ?? 'unknown error'}`);
        }, { once: true });

        return Promise.resolve();
    }

    protected async applyCommit(patch: Partial<AudioClipData>, prev: AudioClipData): Promise<void> {
        if (this._audio) {
            this._audio.loop = this._resolved.loop !== false;
        }
        // A new offset keeps the clip's position in the TIMELINE's time base:
        // what is heard at the current t is re-derived from the new startAt.
        // Presence, not truthiness (DESIGN_PRINCIPLES §10.7): clearing the
        // field (→ 0), in a patch or an undo snapshot, is a change too.
        if ('startAt' in patch || finiteOr0(prev.startAt) !== this._startAt()) {
            const prevStartAt = finiteOr0(prev.startAt);
            const t = this._waitRemaining !== null
                ? prevStartAt - this._waitRemaining
                : (this._audio?.currentTime ?? 0) + prevStartAt;
            this._seekTimeline(Math.max(0, t));
        }
    }

    protected onPlayModeChanged(playing: boolean): void {
        // Leaving play mode stops everything, including a play still queued.
        if (!playing) {
            this._pendingPlayback = false;
            this._stopWait();
        }
        // No element: nothing to start or stop. Autoplay needs no pending
        // mark — every entry point re-derives it from playMode, playOnStart
        // and effectiveActive live.
        if (!this._audio) return;
        if (playing) {
            // Play-mode entry is just the first activation event: only an
            // effectively-active object autoplays. One on a hidden page (or
            // under any disabled ancestor) starts when it actually activates —
            // see onEffectiveActiveChanged — instead of playing from behind a
            // page the user hasn't navigated to yet (#937).
            this._startPlaybackIfDue();
        } else {
            this._dropBlockedRetry();
            this._audio.pause();
            this._positionAt(0);
        }
    }

    /**
     * Activation lifecycle, mirroring Video's onEffectiveActiveChanged so a
     * clip hidden at play-mode entry starts the moment it actually activates.
     * Replaces the old render-loop `_activeObserver` poll — fires immediately
     * on the flip.
     *
     * Deactivation pauses in edit mode too (a clip previewed in the editor
     * falls silent when hidden, as it did under the poll), and drops any
     * autoplay-blocked gesture retry so a later tap elsewhere in the app
     * doesn't resume a clip that went inactive in the meantime. Autoplay
     * resumes in play mode only. Activation is re-entry, as for
     * AnimationSequence: a playOnStart clip resumes on every activation, from
     * where it paused — even one the user paused before it was hidden.
     */
    protected override onEffectiveActiveChanged(active: boolean): void {
        if (!this._audio) return;
        if (!active) {
            this._stopWait();
            this._dropBlockedRetry();
            this._audio.pause();
            return;
        }
        this._startPlaybackIfDue();
    }

    /**
     * The one start condition, shared by every entry point that can start
     * playback on its own — metadata arriving, play-mode entry, activation —
     * so they can't drift apart. A queued explicit play always starts;
     * autoplay needs play mode, playOnStart, and an effectively-active object
     * (#937). Read live, since each entry point can fire while any of those
     * is changing.
     */
    private _shouldStartPlayback(): boolean {
        return this._pendingPlayback || (
            this.playMode &&
            this._resolved.playOnStart !== false &&
            this.sceneObject.effectiveActive
        );
    }

    /** Start playback if {@link _shouldStartPlayback}, consuming a queued play. */
    private _startPlaybackIfDue(): void {
        if (!this._shouldStartPlayback()) return;
        this._pendingPlayback = false;
        this._startPlayback();
    }

    // ── Playable ──

    play(): void {
        if (!this._audio) {
            this._pendingPlayback = true;
            return;
        }
        if (this._waitRemaining !== null) {
            // Before startAt: count down to it; the element starts itself.
            this._startWait();
            return;
        }
        this._audio.loop = this._resolved.loop !== false;
        this._tryPlay();
    }

    pause(): void {
        this._pendingPlayback = false;
        this._stopWait();
        this._dropBlockedRetry();
        this._audio?.pause();
    }

    /** Halt and return to t = 0 of the clip's time base (media `−startAt`, or waiting for `startAt`). */
    stop(): void {
        this._pendingPlayback = false;
        this._stopWait();
        this._dropBlockedRetry();
        this._audio?.pause();
        this._positionAt(0);
    }

    /**
     * Seek in the clip's time base (the driving Timeline's). The media lands
     * at `max(0, t − startAt)`; `t < startAt` leaves the clip silent and
     * waiting. Does not change play/pause intent: a playing clip seeked before
     * `startAt` goes silent and starts again at `startAt`; a waiting clip
     * seeked past it starts sounding. `t` is clamped at 0.
     */
    seek(time: number, mode: "absolute" | "relative" = "absolute"): void {
        const target = mode === "relative" ? this._currentClipTime() + time : time;
        this._seekTimeline(Math.max(0, target));
    }

    /**
     * PlayableClock: `element.currentTime + startAt` while the element is
     * actually producing time; null before startAt, while paused/ended,
     * seeking, stalled/buffering, or before the element exists.
     */
    getClockTime(): number | null {
        if (this._waitRemaining !== null) return null;
        return mediaElementClockTime(this._audio, this._startAt());
    }

    /** Set element volume (0-1). For spatial volume control use AudioSource. */
    setVolume(volume: number): void {
        if (!this._audio) return;
        this._audio.volume = Math.max(0, Math.min(1, volume));
    }

    /**
     * Whether the clip is currently playing (not paused, not ended). False
     * while waiting for `startAt` — the clip is silent then.
     */
    isPlaying(): boolean {
        return !!this._audio && !this._audio.paused && !this._audio.ended;
    }

    /** Current element volume (0-1). Returns 1 when no element is loaded. */
    getVolume(): number {
        return this._audio?.volume ?? 1;
    }

    /**
     * Swap the underlying audio source URL at runtime. Pauses playback and
     * resets the element to load the new URL. Authored `sceneObject.src` is
     * left unchanged (this is a runtime-only override).
     *
     * Idempotent for direct (non-blob) URLs: returns immediately if the URL
     * matches the current source. Blob-backed inputs always reload, since
     * `getAsset` returns a Blob and the comparison would be between
     * object-URLs which are minted per call.
     *
     * Does NOT auto-play. Callers must drive play state explicitly — the
     * media-control blocks (`trace/playMedia`) await this promise then call
     * `play()`.
     *
     * The returned Promise resolves when the element has metadata available
     * (the same point `onMediaElementReadyObservable` fires).
     */
    public setSource(url: string): Promise<void> {
        if (!url) {
            Logger.warn(`[AudioClip] setSource called with empty URL on "${this.sceneObject.name}"`);
            return Promise.resolve();
        }
        // Resolve via ResourceManager the same way `load()` does, so callers
        // can pass either a registered local path or an HTTP(S) URL.
        const blob = this.resources.getAsset(url);

        // Idempotency check: only meaningful for direct URLs. Blob inputs
        // produce a fresh object-URL each call, so the comparison can never
        // match — skipping the check avoids wasteful createObjectURL/revoke
        // pair on the no-op path.
        if (!blob && this._resolvedUrl === url) {
            return Promise.resolve();
        }

        let resolved = url;
        let ownsObjectUrl = false;
        if (blob) {
            resolved = URL.createObjectURL(blob);
            ownsObjectUrl = true;
        }

        this._dropBlockedRetry();
        this._stopWait();
        if (this._audio) {
            this._audio.pause();
            // Drop refs to old element so the browser stops buffering immediately.
            this._audio.removeAttribute('src');
            this._audio.load();
        }
        if (this._ownsObjectUrl && this._resolvedUrl) {
            URL.revokeObjectURL(this._resolvedUrl);
        }

        this._resolvedUrl = resolved;
        this._ownsObjectUrl = ownsObjectUrl;
        this._loaded = false;
        const generation = ++this._loadGeneration;

        const el = this._audio ?? new window.Audio();
        if (!this._audio) {
            el.preload = "metadata";
            el.crossOrigin = "anonymous";
            this._audio = el;
        }
        el.src = resolved;
        el.loop = this._resolved.loop !== false;
        this._positionAt(0);

        return new Promise<void>((resolve) => {
            const onReady = () => {
                // Bail if a later setSource superseded this one. The newer call
                // will have set _loadGeneration > generation and registered its
                // own listener; resolving here would claim "ready" for the wrong
                // source. Resolve anyway so the caller's await unblocks rather
                // than hangs — they'll observe stale state if they act on it,
                // which is preferable to a leaked Promise.
                if (this._loadGeneration !== generation) {
                    resolve();
                    return;
                }
                if (this._loaded) {
                    resolve();
                    return;
                }
                this._loaded = true;
                this.onMediaElementReadyObservable.notifyObservers(el);
                resolve();
            };
            if (el.readyState >= 1) {
                onReady();
            } else {
                el.addEventListener('loadedmetadata', onReady, { once: true });
            }
        });
    }

    private _tryPlay(): void {
        const el = this._audio;
        if (!el) return;
        this._dropBlockedRetry();
        const request = this._playRequest;
        const audio = this.sceneObject.traceScene.getAudioManager();
        // When this play comes from a user action (trace/playMedia on tap),
        // resuming here keeps it inside the gesture: an element routed through
        // AudioSource plays into the shared context, silent while suspended.
        audio.resume();
        el.play().catch((err: unknown) => {
            if ((err as { name?: string } | null)?.name !== "NotAllowedError") return;
            if (this._audio !== el || this._playRequest !== request) return;
            this._cancelBlockedRetry = audio.retryOnGesture(() => {
                this._cancelBlockedRetry = null;
                if (this._audio === el && el.paused) {
                    el.play().catch(() => { /* refused again: stay silent, no loop */ });
                }
            });
        });
    }

    // ── startAt ──

    private _startAt(): number {
        return finiteOr0(this._resolved.startAt);
    }

    /** Current position in the clip's time base. */
    private _currentClipTime(): number {
        const startAt = this._startAt();
        if (this._waitRemaining !== null) return startAt - this._waitRemaining;
        return (this._audio?.currentTime ?? 0) + startAt;
    }

    /**
     * Place the clip at time-base `t` without touching play intent. Before
     * startAt the element is paused at 0 (silent) and the wait is armed;
     * otherwise the element is placed at `t − startAt`, clamped to [0, duration].
     */
    private _positionAt(t: number): void {
        const startAt = this._startAt();
        if (t < startAt) {
            this._waitRemaining = startAt - t;
            if (this._audio) {
                if (!this._audio.paused) {
                    this._dropBlockedRetry();
                    this._audio.pause();
                }
                this._audio.currentTime = 0;
            }
            return;
        }
        this._waitRemaining = null;
        if (this._audio) {
            const media = t - startAt;
            const duration = this._audio.duration;
            const upper = isFinite(duration) ? Math.min(media, duration) : media;
            this._audio.currentTime = Math.max(0, upper);
        }
    }

    /** Seek that carries play intent across the startAt boundary (see seek()). */
    private _seekTimeline(t: number): void {
        const active = this._waitObserver !== null || this.isPlaying();
        this._positionAt(t);
        if (!active) return;
        if (this._waitRemaining !== null) {
            this._startWait();
        } else if (this._waitObserver) {
            this._stopWait();
            this._tryPlay();
        }
    }

    /** Begin playback honoring startAt (the playOnStart / re-enable path). */
    private _startPlayback(): void {
        if (this._waitRemaining !== null) this._startWait();
        else this._tryPlay();
    }

    private _startWait(): void {
        if (this._waitObserver || !this.scene) return;
        this._waitObserver = this.scene.onBeforeRenderObservable.add(() => {
            if (this._waitRemaining === null) {
                this._stopWait();
                return;
            }
            this._waitRemaining -= this.scene.getEngine().getDeltaTime() / 1000;
            if (this._waitRemaining > 0) return;
            // Reached startAt: start the element, carrying the frame's overshoot
            // so it lands where the (scene-time) Timeline is.
            const overshoot = -this._waitRemaining;
            this._stopWait();
            this._positionAt(this._startAt() + overshoot);
            if (!this._audio) {
                this._pendingPlayback = true;
                return;
            }
            this._audio.loop = this._resolved.loop !== false;
            this._tryPlay();
        });
    }

    private _stopWait(): void {
        if (this._waitObserver) {
            this.scene?.onBeforeRenderObservable.remove(this._waitObserver);
            this._waitObserver = null;
        }
    }

    private _dropBlockedRetry(): void {
        this._playRequest++;
        const cancel = this._cancelBlockedRetry;
        this._cancelBlockedRetry = null;
        cancel?.();
    }

    protected onDetach(): void {
        this._stopWait();
        this._dropBlockedRetry();
        if (this._audio) {
            this._audio.pause();
            // Drop the source so the browser stops buffering immediately.
            this._audio.removeAttribute('src');
            this._audio.load();
            this._audio = null;
        }
        if (this._ownsObjectUrl && this._resolvedUrl) {
            URL.revokeObjectURL(this._resolvedUrl);
        }
        this._resolvedUrl = null;
        this._ownsObjectUrl = false;
        this._loaded = false;
        this.onMediaElementReadyObservable.clear();
    }
}

function finiteOr0(v: unknown): number {
    return typeof v === "number" && Number.isFinite(v) ? v : 0;
}
